import React, { useState, useEffect, useCallback } from 'react';
import { 
  Modal, 
  Box, 
  TextField, 
  Button, 
  Typography, 
  IconButton, 
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ModalBox } from '../../../styles/ModalStyles';
import CloseIcon from '@mui/icons-material/Close';
import PaletteIcon from '@mui/icons-material/Palette';
import { 
  getClienteHabilitado,
  validarModulosCompromisosObligaciones,
  validarClienteTieneModuloSemaforo
} from '../../http/actions/ClientesAction';

import ColorPickerComponent from '../../common/ColorPickerComponent';

const CrearSemaforoModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  configuracionesExistentes = []
}) => {
  const [formData, setFormData] = useState({
    idCliente: 0,
    colorAlta: '#FF0000',
    colorMedia: '#FFFF00',
    colorBaja: '#00FF00',
    cantidadAlta: 0,
    cantidadMedia: 0,
    cantidadBaja: 0,
    tipoAplicacion: 'Ambos',
    habilitado: true,
  });

  const [clientesDisponibles, setClientesDisponibles] = useState([]);
  const [errors, setErrors] = useState({});
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [modulosCliente, setModulosCliente] = useState(null);
  const [validandoCliente, setValidandoCliente] = useState(false);
  const [tieneModuloSemaforo, setTieneModuloSemaforo] = useState(null);

  const resetAllStates = () => {
    setFormData({
      idCliente: 0,
      colorAlta: '#FF0000',
      colorMedia: '#FFFF00',
      colorBaja: '#00FF00',
      cantidadAlta: 0,
      cantidadMedia: 0,
      cantidadBaja: 0,
      tipoAplicacion: 'Ambos',
      habilitado: true,
    });
    setErrors({});
    setClientesDisponibles([]);
    setModulosCliente(null);
    setTieneModuloSemaforo(null);
    setValidandoCliente(false);
    setLoadingClientes(false);
  };

  useEffect(() => {
    if (open) {
      resetAllStates();
      fetchClientesDisponibles();
    }
  }, [open]);

  const fetchClientesDisponibles = async () => {
    setLoadingClientes(true);
    try {
      const clientes = await getClienteHabilitado(localStorage.getItem("token"));
      setClientesDisponibles(Array.isArray(clientes) ? clientes : []);
    } catch (error) {
      console.error("Error al cargar clientes:", error);
      setClientesDisponibles([]);
    } finally {
      setLoadingClientes(false);
    }
  };

  const validarModulosCliente = useCallback(async (idCliente) => {
    setValidandoCliente(true);
    setModulosCliente(null);
    try {
      const modulos = await validarModulosCompromisosObligaciones(localStorage.getItem("token"), idCliente);
      setModulosCliente(modulos);
      
      // Ajustar tipoAplicacion automáticamente
      if (modulos.tieneCompromisos && !modulos.tieneObligaciones) {
        setFormData(prev => ({ ...prev, tipoAplicacion: 'Compromisos' }));
      } else if (!modulos.tieneCompromisos && modulos.tieneObligaciones) {
        setFormData(prev => ({ ...prev, tipoAplicacion: 'Obligaciones' }));
      } else if (modulos.tieneCompromisos && modulos.tieneObligaciones) {
        setFormData(prev => ({ ...prev, tipoAplicacion: 'Ambos' }));
      }
    } catch (error) {
      console.error("Error al validar módulos del cliente:", error);
      setModulosCliente({ tieneCompromisos: false, tieneObligaciones: false });
    } finally {
      setValidandoCliente(false);
    }
  }, []);

  const validarSemaforoCliente = async (idCliente) => {
    try {
      const tieneSemaforo = await validarClienteTieneModuloSemaforo(localStorage.getItem("token"), idCliente);
      setTieneModuloSemaforo(tieneSemaforo);
    } catch (error) {
      console.error("Error al validar módulo de semáforo:", error);
      setTieneModuloSemaforo(false);
    }
  };

  useEffect(() => {
    if (formData.idCliente > 0) {
      validarModulosCliente(formData.idCliente);
      validarSemaforoCliente(formData.idCliente);
    } else {
      setModulosCliente(null);
      setTieneModuloSemaforo(null);
      setValidandoCliente(false);
    }
  }, [formData.idCliente, validarModulosCliente]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.idCliente === 0) {
      newErrors.idCliente = 'Debe seleccionar un cliente';
    }

    if (tieneModuloSemaforo === false) {
      newErrors.idCliente = 'El cliente no tiene asignado el módulo de Semáforo. Debe asignarlo desde la gestión de módulos.';
    }

    if (modulosCliente && !modulosCliente.tieneCompromisos && !modulosCliente.tieneObligaciones) {
      newErrors.idCliente = 'El cliente no tiene contratados los módulos de Compromisos ni Obligaciones';
    }

    if (formData.idCliente !== 0) {
      const clienteExiste = configuracionesExistentes.some(
        config => config.idCliente === formData.idCliente
      );
      
      if (clienteExiste) {
        newErrors.idCliente = 'Ya existe una configuración de semáforo para este cliente';
      }
    }

    if (modulosCliente && formData.tipoAplicacion === "Compromisos" && !modulosCliente.tieneCompromisos) {
      newErrors.tipoAplicacion = 'El cliente no tiene contratado el módulo de Compromisos';
    }
    
    if (modulosCliente && formData.tipoAplicacion === "Obligaciones" && !modulosCliente.tieneObligaciones) {
      newErrors.tipoAplicacion = 'El cliente no tiene contratado el módulo de Obligaciones';
    }

    if (formData.cantidadMedia <= 0) {
      newErrors.cantidadMedia = 'La cantidad debe ser mayor a 0';
    }
    if (formData.cantidadAlta <= 0) {
      newErrors.cantidadAlta = 'La cantidad debe ser mayor a 0';
    }

    if (formData.cantidadBaja > 0 && formData.cantidadMedia > 0 && 
        formData.cantidadBaja >= formData.cantidadMedia) {
      newErrors.cantidadMedia = 'La cantidad media debe ser mayor que la baja';
    }
    
    if (formData.cantidadMedia > 0 && formData.cantidadAlta > 0 && 
        formData.cantidadMedia >= formData.cantidadAlta) {
      newErrors.cantidadAlta = 'La cantidad alta debe ser mayor que la media';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getTipoAplicacionOptions = () => {
    if (!modulosCliente) return [];
    
    const options = [];
    
    if (modulosCliente.tieneCompromisos) {
      options.push({ value: "Compromisos", label: "Solo Compromisos" });
    }
    
    if (modulosCliente.tieneObligaciones) {
      options.push({ value: "Obligaciones", label: "Solo Obligaciones" });
    }
    
    if (modulosCliente.tieneCompromisos && modulosCliente.tieneObligaciones) {
      options.push({ value: "Ambos", label: "Compromisos y Obligaciones" });
    }
    
    return options;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const handleClose = () => {
    resetAllStates();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalBox sx={{ maxWidth: 700, width: '95%', maxHeight: '90vh', overflow: 'auto' }}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant='h6' sx={{ mb: 2, pr: 4 }}>
          Crear Nueva Configuración de Semáforo
        </Typography>

        <form onSubmit={handleSubmit}>
          {/* Selección de Cliente */}
          <Box sx={{ mb: 4 }}>
            <FormControl fullWidth error={!!errors.idCliente}>
              <InputLabel>Seleccionar Cliente</InputLabel>
              <Select
                value={formData.idCliente}
                label="Seleccionar Cliente"
                onChange={(e) => handleInputChange('idCliente', e.target.value)}
                disabled={loadingClientes}
              >
                <MenuItem value={0}>
                  <em>Seleccione un cliente</em>
                </MenuItem>
                {clientesDisponibles.map((cliente) => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    {cliente.nombre}
                  </MenuItem>
                ))}
              </Select>
              {errors.idCliente && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {errors.idCliente}
                </Typography>
              )}
            </FormControl>

            {/* Validación de módulos */}
            {formData.idCliente > 0 && (
              <Box sx={{ mt: 2 }}>
                {validandoCliente && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="caption">Validando módulos contratados...</Typography>
                  </Box>
                )}
                
                {!validandoCliente && tieneModuloSemaforo !== null && (
                  <Box sx={{ mb: 2 }}>
                    {tieneModuloSemaforo ? (
                      <Alert severity="success" sx={{ mb: 1 }}>
                        ✓ El cliente tiene asignado el módulo de Semáforo
                      </Alert>
                    ) : (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        ⚠ El cliente NO tiene asignado el módulo de Semáforo. 
                        Debe asignarlo desde la gestión de módulos antes de configurar el semáforo.
                      </Alert>
                    )}
                  </Box>
                )}
                
                {!validandoCliente && modulosCliente && tieneModuloSemaforo && (
                  <>
                    {(modulosCliente.tieneCompromisos || modulosCliente.tieneObligaciones) ? (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Módulos contratados:</strong>
                          <br />
                          {modulosCliente.tieneCompromisos && "✓ Compromisos"}
                          {modulosCliente.tieneCompromisos && modulosCliente.tieneObligaciones && <br />}
                          {modulosCliente.tieneObligaciones && "✓ Obligaciones"}
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        ⚠ El cliente no tiene contratados los módulos de Compromisos ni Obligaciones. 
                        No se puede configurar el semáforo hasta que contrate alguno de estos módulos.
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Tipo de Aplicación */}
          {modulosCliente && (modulosCliente.tieneCompromisos || modulosCliente.tieneObligaciones) && (
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth error={!!errors.tipoAplicacion}>
                <InputLabel>Aplicar Semáforo a</InputLabel>
                <Select
                  value={formData.tipoAplicacion}
                  label="Aplicar Semáforo a"
                  onChange={(e) => handleInputChange('tipoAplicacion', e.target.value)}
                >
                  {getTipoAplicacionOptions().map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.tipoAplicacion && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.tipoAplicacion}
                  </Typography>
                )}
              </FormControl>
            </Box>
          )}

          {/* Configuración de Colores - NUEVA IMPLEMENTACIÓN */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <PaletteIcon sx={{ mr: 1 }} />
              Configuración de Colores
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <ColorPickerComponent
                  color={formData.colorBaja}
                  onChange={(color) => handleInputChange('colorBaja', color)}
                  label="Color Criticidad Baja"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ColorPickerComponent
                  color={formData.colorMedia}
                  onChange={(color) => handleInputChange('colorMedia', color)}
                  label="Color Criticidad Media"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ColorPickerComponent
                  color={formData.colorAlta}
                  onChange={(color) => handleInputChange('colorAlta', color)}
                  label="Color Criticidad Alta"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Configuración de Umbrales */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Configuración de Umbrales de Criticidad
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Umbral Criticidad Baja"
                  type="number"
                  value={formData.cantidadBaja}
                  onChange={(e) => handleInputChange('cantidadBaja', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  error={!!errors.cantidadBaja}
                  helperText={errors.cantidadBaja || "Cantidad mínima para criticidad baja"}
                  required
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Umbral Criticidad Media"
                  type="number"
                  value={formData.cantidadMedia}
                  onChange={(e) => handleInputChange('cantidadMedia', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  error={!!errors.cantidadMedia}
                  helperText={errors.cantidadMedia || "Cantidad mínima para criticidad media"}
                  required
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Umbral Criticidad Alta"
                  type="number"
                  value={formData.cantidadAlta}
                  onChange={(e) => handleInputChange('cantidadAlta', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  error={!!errors.cantidadAlta}
                  helperText={errors.cantidadAlta || "Cantidad mínima para criticidad alta"}
                  required
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Ejemplo:</strong> Si configura Baja=1, Media=5, Alta=10:
                <br />• 1-4 elementos = Criticidad Baja (color verde)
                <br />• 5-9 elementos = Criticidad Media (color amarillo)
                <br />• 10+ elementos = Criticidad Alta (color rojo)
              </Typography>
            </Alert>
          </Box>

          {/* Estado */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.habilitado}
                  onChange={(e) => handleInputChange('habilitado', e.target.checked)}
                />
              }
              label="Configuración de Semáforo Habilitada"
            />
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}> 
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={
                formData.idCliente === 0 || 
                !modulosCliente || 
                (!modulosCliente.tieneCompromisos && !modulosCliente.tieneObligaciones) ||
                tieneModuloSemaforo === false
              }
            >
              Crear Configuración
            </Button>
            <Button 
              onClick={handleClose} 
              variant="contained" 
              color="error"
            >
              Cancelar
            </Button>
          </Box>
        </form>
      </ModalBox>
    </Modal>
  );
};

export default CrearSemaforoModal;